import React from 'react';
import { useHover } from 'react-aria';
import { useTheme } from 'styled-components';
import * as S from './styles';
import { PlayButtonOverlayProps } from './types';

/**
 * A playback button inteded to be used as an overlay on top of another component -
 * over a video, card, etc.
 *
 * The button will reflect the action that will occur when it is pressed given
 * the current playback state.  For example, when `playing == true`, the button
 * will show a pause icon, will update its `aria-label` to indicate that it's a
 * pause button, and it will pause playback when clicked.
 *
 * If `duration` and `currentTime` are passed, the button will show a progress
 * ring around its border during playback.
 */
function PlayButtonOverlay({
  as,
  buttonAs,
  className,
  currentTime,
  duration,
  playing,
  ...props
}: PlayButtonOverlayProps): React.ReactElement | null {
  const { hoverProps, isHovered } = useHover({});
  const theme = useTheme();

  return (
    <S.Root
      as={as ?? 'div'}
      className={className}
      data-hovered={isHovered}
      $duration={duration}
      $playing={playing}
      {...hoverProps}
    >
      <S.Button
        forwardedAs={buttonAs ?? 'button'}
        aria-label={playing ? 'pause' : 'play'}
        $duration={duration}
        $playing={playing}
        {...props}
      >
        {playing ? <S.PauseIcon /> : <S.PlayIcon />}
      </S.Button>
      {duration !== undefined && playing && (
        <S.Progress
          aria-label="playback progress"
          fillColor={theme.palette.common.white}
          fillWidth={4}
          hideLabel
          valueLabel={`${currentTime ?? 0}`}
          maxValue={duration}
          minValue={0}
          trackColor="transparent"
          value={currentTime}
        />
      )}
    </S.Root>
  );
}

export default PlayButtonOverlay;
